import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowRight, CarFront} from 'lucide-react';
import {cn} from '@chekinapp/ui';

type PromoCardProps = {
  imageUrl?: string;
  overlayClassName: string;
  kicker: ReactNode;
  title: string;
  description: string;
  footer?: ReactNode;
  ctaLabel: string;
  ctaClassName: string;
  onClick: () => void;
  className?: string;
  'data-testid'?: string;
};

function PromoCard({
  imageUrl,
  overlayClassName,
  kicker,
  title,
  description,
  footer,
  ctaLabel,
  ctaClassName,
  onClick,
  className,
  ...props
}: PromoCardProps) {
  return (
    <div
      className={cn(
        'relative isolate flex min-h-[220px] flex-col overflow-hidden rounded-[18px] bg-[var(--chekin-color-brand-navy)] p-3 text-[var(--chekin-color-white)] lg:min-h-0 lg:px-4 lg:py-3.5',
        className,
      )}
      data-testid={props['data-testid']}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 -z-20 size-full object-cover"
        />
      )}
      <span aria-hidden className={cn('absolute inset-0 -z-10', overlayClassName)} />
      {kicker}
      <h4 className="mt-2 text-[16px] font-extrabold leading-[19px] tracking-[-0.01em] lg:text-[18px] lg:leading-[22px]">
        {title}
      </h4>
      <p className="mt-1 line-clamp-3 text-[12px] font-medium leading-4 opacity-90">
        {description}
      </p>
      {footer}
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'mt-auto inline-flex h-9 w-full items-center justify-center gap-1 rounded-[12px] px-2 text-[13px] font-semibold lg:gap-1.5 lg:px-3 transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--chekin-color-white)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent lg:mt-3',
          ctaClassName,
        )}
      >
        <span className="truncate">{ctaLabel}</span>
        <ArrowRight size={14} className="shrink-0" />
      </button>
    </div>
  );
}

const KICKER_CLASS =
  'inline-flex w-fit max-w-full items-center gap-1.5 text-[12px] font-extrabold leading-4 lg:uppercase lg:tracking-[0.1em]';

type ExtraCardProps = {
  imageUrl?: string;
  onClick: () => void;
  className?: string;
};

export function TransferCard({imageUrl, onClick, className}: ExtraCardProps) {
  const {t} = useTranslation();

  return (
    <PromoCard
      data-testid="home-transfer-card"
      imageUrl={imageUrl}
      overlayClassName="bg-[linear-gradient(100deg,var(--chekin-color-brand-navy)_35%,color-mix(in_srgb,var(--chekin-color-brand-navy)_35%,transparent))]"
      kicker={
        <span className={KICKER_CLASS}>
          <CarFront size={14} className="shrink-0" />
          <span className="truncate">{t('home_transfer_kicker')}</span>
        </span>
      }
      title={t('home_transfer_title')}
      description={t('home_transfer_description')}
      footer={
        <span className="mt-3 hidden items-center gap-2 text-[12px] font-extrabold uppercase tracking-[0.06em] lg:flex">
          <span>{t('home_transfer_from')}</span>
          <span className="relative h-0.5 flex-1 rounded-full bg-[repeating-linear-gradient(90deg,color-mix(in_srgb,var(--chekin-color-white)_75%,transparent)_0_5px,transparent_5px_10px)]">
            <span className="absolute left-1/2 top-1/2 grid size-[22px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-[var(--chekin-amber-400)] text-[var(--chekin-color-brand-navy)]">
              <CarFront size={12} />
            </span>
          </span>
          <span>{t('home_transfer_to')}</span>
        </span>
      }
      ctaLabel={t('home_transfer_cta')}
      ctaClassName="bg-[var(--chekin-color-white)] text-[var(--chekin-color-brand-navy)]"
      onClick={onClick}
      className={className}
    />
  );
}

export function ToursCard({imageUrl, onClick, className}: ExtraCardProps) {
  const {t} = useTranslation();

  return (
    <PromoCard
      data-testid="home-tours-card"
      imageUrl={imageUrl}
      overlayClassName="bg-[linear-gradient(180deg,color-mix(in_srgb,var(--chekin-color-brand-navy)_20%,transparent),color-mix(in_srgb,var(--chekin-color-brand-navy)_85%,transparent)_70%)]"
      kicker={
        <span
          className={cn(
            KICKER_CLASS,
            'rounded-full bg-[color-mix(in_srgb,var(--chekin-color-brand-navy)_90%,transparent)] px-2.5 py-1',
          )}
        >
          <b className="text-[var(--chekin-amber-400)]">{t('free')}</b>
          <span className="truncate">{t('home_tours_kicker')}</span>
        </span>
      }
      title={t('home_tours_title')}
      description={t('home_tours_description')}
      ctaLabel={t('home_tours_cta')}
      ctaClassName="bg-[var(--chekin-amber-400)] text-[var(--chekin-color-brand-navy)]"
      onClick={onClick}
      className={className}
    />
  );
}
